/**
 * The React side of the one poll store (`poll-store.ts`): `PollStoreContext` is mounted once per
 * app in `src/components/Layout.tsx`, inside `CommentHubAPIContext.Provider` (it reads `api` and
 * `site`) and under the app's auth provider (`_app.tsx`). `usePoll` / `usePollVote` are what a
 * poll surface uses; without the provider (a jest render, a page outside Layout) they draw the
 * page data and a tap does nothing.
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { createContainer } from 'shared-components/unstated'
import type { PollSetKindName, PollVotedFrom } from 'shared-code/app-events'
import type { DSPollChoiceCount, DSPoll, DSPostContext } from 'shared-definitions/types'
import { AppEventsContext } from '../contexts/AppEventsContext'
import { AuthContext } from '../contexts/AuthContext'
import { CommentHubAPIContext } from '../contexts/CommentHubAPIContext'
import { CommonDataContext } from '../contexts/CommonDataContext'
import { NotificationsContext } from '../contexts/NotificationsContext'
import {
  browserPollStorage,
  PollHubTransport,
  PollState,
  pollStateFrom,
  PollStore,
  PollStoreDeps,
  PollVoteMeta,
  PollVoteOutcome,
  referrerKind,
} from './poll-store'

/** How the reader arrived, read once per page — the same value on every vote of the visit. */
let pageReferrerKind: string | null = null
function readReferrerKind(): string | undefined {
  if (typeof document === 'undefined' || typeof location === 'undefined') {
    return undefined
  }
  pageReferrerKind ??= referrerKind(document.referrer, location.hostname)
  return pageReferrerKind
}

/** The vote's context for commentHub's event log: the article and the referrer kind. */
function voteMeta(postMeta: DSPostContext | null): PollVoteMeta {
  return { articlePostId: postMeta?.postId ?? null, referrerKind: readReferrerKind() }
}

/** The app's WordPress poll calls — each Layout passes its data source's. */
export interface PollStoreProps {
  sendPoll: (feed: {
    pollId: string
    choiceId: string
  }) => Promise<{ code: number; choices?: readonly DSPollChoiceCount[] }>
  getPoll: (pollId: string) => Promise<readonly DSPollChoiceCount[] | null>
}

export const SIGN_IN_TO_CHANGE_VOTE = {
  title: 'Sign in to change your vote',
  subtitle: 'Signed in, you can change your pick any time — and it shows on your comments.',
}

export const PollStoreContext = createContainer<PollStore, PollStoreProps>(initial => {
  const commentHub = CommentHubAPIContext.useOptionalContainer()
  const auth = AuthContext.useOptionalContainer()
  const ref = useRef<PollStore | null>(null)
  if (!ref.current) {
    const api = commentHub?.api ?? null
    const site = commentHub?.site ?? null
    const hub: PollHubTransport | null =
      api && site
        ? {
            vote: (token, pollId, choiceId, alreadyCounted, context) =>
              api.votePoll(token, site, pollId, choiceId, alreadyCounted, context),
            getVote: (token, pollId) => api.getPollVote(token, site, pollId),
            getVotes: (token, pollIds) => api.getPollVotes(token, site, pollIds),
            remove: (token, pollId, context) => api.removePollVote(token, site, pollId, context),
            voteAnon: (anonId, pollId, choiceId, context) =>
              api.votePollAnon(site, pollId, anonId, choiceId, context),
          }
        : null
    const deps: PollStoreDeps = {
      wp: {
        sendVote: async (pollId, choiceId) => {
          if (!initial) {
            return { ok: false }
          }
          const response = await initial.sendPoll({ pollId, choiceId })
          return { ok: response.code === 200, choices: response.choices }
        },
        fetchCounts: pollId => (initial ? initial.getPoll(pollId) : Promise.resolve(null)),
      },
      hub,
      storage: browserPollStorage,
    }
    ref.current = new PollStore(deps)
  }
  const store = ref.current
  const token = auth?.authState?.token ?? null
  useEffect(() => {
    store.setToken(token)
  }, [store, token])
  return store
})

/** A poll's live state: the store's once registered (after mount), the page data before. */
export function usePoll(poll: DSPoll | null): PollState | null {
  const store = PollStoreContext.useOptionalContainer()
  useEffect(() => {
    if (store && poll) {
      store.register(poll)
    }
  }, [store, poll])
  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => undefined),
    [store]
  )
  const id = poll?.id ?? null
  const live = useSyncExternalStore(
    subscribe,
    () => (store && id ? store.get(id) : undefined),
    () => undefined
  )
  const seedRef = useRef<{ poll: DSPoll; state: PollState } | null>(null)
  if (!poll) {
    return null
  }
  if (live) {
    return live
  }
  if (seedRef.current?.poll !== poll) {
    seedRef.current = { poll, state: pollStateFrom(poll) }
  }
  return seedRef.current.state
}

/** Fires the analytics for a tap's outcome or shows the generic error — shared by the tap
 *  (`usePollVote`) and the Undo (`usePollUndo`). A signed-out re-tap needs nothing here: the
 *  store's `signInAsk` draws the inline card (`PollFootnote`), and only its button opens the
 *  login dialog (Guillaume, 2026-10-25). */
function usePollOutcome(
  poll: DSPoll | null,
  votedFrom: PollVotedFrom,
  postMeta: DSPostContext | null,
  setKind?: PollSetKindName
): (outcome: PollVoteOutcome) => void {
  const appEvents = AppEventsContext.useOptionalContainer()
  const notifications = NotificationsContext.useOptionalContainer()
  const common = CommonDataContext.useOptionalContainer()
  const errGeneral = common?.common.strings.errGeneral
  return useCallback(
    (outcome: PollVoteOutcome) => {
      if (!poll) {
        return
      }
      if (outcome.kind === 'sign-in') {
        return
      }
      if (outcome.kind === 'failed') {
        if (errGeneral) {
          notifications?.enqueue({ msg: errGeneral, duration: 3000, variant: 'error' })
        }
        return
      }
      if (outcome.kind === 'unchanged' || !postMeta) {
        return
      }
      const answer = poll.items.find(c => c.id === outcome.choiceId)?.answer ?? ''
      if (outcome.kind === 'removed') {
        appEvents?.appEvent({
          url: window.location.href,
          event: 'poll-vote-removed',
          postMeta,
          question: poll.question,
          answer,
          responsesCount: outcome.total,
          pollId: poll.id,
          removedFrom: votedFrom,
          ...(setKind ? { setKind } : {}),
        })
        return
      }
      appEvents?.appEvent({
        url: window.location.href,
        event: 'poll-voted',
        postMeta,
        question: poll.question,
        answer,
        responsesCount: outcome.total,
        pollId: poll.id,
        votedFrom,
        changed: outcome.changed,
        ...(setKind ? { setKind } : {}),
      })
    },
    [poll, notifications, errGeneral, appEvents, postMeta, votedFrom, setKind]
  )
}

/**
 * The tap on a choice, on every surface: votes, changes or (signed in, the own pick again)
 * removes through the store, then reports it once (`poll-voted` / `poll-vote-removed`) or shows
 * the generic error; a signed-out re-tap raises the store's sign-in card. Resolves to the
 * outcome (null without a store) for a surface that reacts to it (the mobile top bar opens the
 * panel after a vote).
 */
export function usePollVote(
  poll: DSPoll | null,
  votedFrom: PollVotedFrom,
  postMeta: DSPostContext | null,
  /** "Have your say" only: the set the poll sits in, on the event. */
  setKind?: PollSetKindName
): (choiceId: string) => Promise<PollVoteOutcome | null> {
  const store = PollStoreContext.useOptionalContainer()
  const report = usePollOutcome(poll, votedFrom, postMeta, setKind)
  return useCallback(
    async (choiceId: string) => {
      if (!store || !poll) {
        return null
      }
      const outcome = await store.vote(poll.id, choiceId, votedFrom, voteMeta(postMeta))
      report(outcome)
      return outcome
    },
    [store, poll, votedFrom, report, postMeta]
  )
}

/** "Undo" after a removal: the removed choice, voted again (reported as a `poll-voted`). */
export function usePollUndo(
  poll: DSPoll | null,
  votedFrom: PollVotedFrom,
  postMeta: DSPostContext | null,
  setKind?: PollSetKindName
): () => Promise<void> {
  const store = PollStoreContext.useOptionalContainer()
  const report = usePollOutcome(poll, votedFrom, postMeta, setKind)
  return useCallback(async () => {
    if (!store || !poll) {
      return
    }
    report(await store.undoRemove(poll.id, votedFrom, voteMeta(postMeta)))
  }, [store, poll, votedFrom, report, postMeta])
}

/**
 * The composer posted a comment with the reader's pick on this poll: the store's `posted`, so
 * every surface swaps "Why this one?" for `POSTED_LINE`. A no-op without a store or a poll.
 */
export function usePollMarkPosted(poll: DSPoll | null): () => void {
  const store = PollStoreContext.useOptionalContainer()
  const id = poll?.id ?? null
  return useCallback(() => {
    if (store && id) {
      store.markPosted(id)
    }
  }, [store, id])
}
